import { useCallback, useMemo, useState } from 'react';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType, extractBrandInsightsIds, type BrandInsightsInstanceContext, type ChatSource, type RecordFilter } from '@trinity/chat';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';
import * as Presentation from './Presentation';

type Props = {
  onChange?: (value: RecordFilter.Item[]) => unknown;
  readonly?: boolean;
  value: RecordFilter.Item[] | RecordFilter.Item | null;
  context?: BrandInsightsInstanceContext.ItemWithMetadata;
  projectIds?: number[];
  sourceTypes?: ChatSource[];
};

export const Record = ({ context, onChange, projectIds = [], readonly, sourceTypes = [], value }: Props) => {
  const [input, setInput] = useState('');
  const queryInput = useDebounceValue(input, 300);
  const normalizedQueryInput = useMemo(() => queryInput.trim(), [queryInput]);
  const apiClient = useChatApiClient();
  const contextKey = useMemo(() => {
    if (!context) return null;

    const { brandId, fileId, folderId, projectId, transcriptId } = extractBrandInsightsIds(context);
    return JSON.stringify({
      type: context.type,
      identifier: context.type === ChatContextType.Agent ? context.data.identifier : null,
      brandId,
      folderId,
      projectId,
      fileId,
      transcriptId,
    });
  }, [context]);

  const searchEnabled = useMemo(() => {
    return !!context && projectIds.length > 0 && normalizedQueryInput.length > 0;
  }, [context, projectIds.length, normalizedQueryInput.length]);

  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useQuery({
    queryKey: ['get:chat/records', contextKey, normalizedQueryInput, projectIds, sourceTypes],
    queryFn: async () => {
      if (!context) {
        return { results: [] };
      }

      return apiClient.chat.searchChatRecords({
        context,
        query: normalizedQueryInput,
        projectIds,
        sourceTypes,
      });
    },
    placeholderData: {
      results: [],
    },
    enabled: searchEnabled,
    refetchOnWindowFocus: false,
  });

  const loadingMessage = useCallback(() => {
    return 'Searching...';
  }, []);

  const noOptionsMessage = useCallback(() => {
    if (projectIds.length === 0) return 'Select a project to search records';
    if (!normalizedQueryInput.length) return 'Start typing to search';
    return 'No records found for selected project';
  }, [normalizedQueryInput.length, projectIds.length]);

  const handleChange = useCallback((records: readonly RecordFilter.Item[]) => {
    return onChange?.([...records]);
  }, [onChange]);

  const theme = useTheme();

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const normalizedValue = useMemo<RecordFilter.Item[]>(() => {
    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }, [value]);

  const isSearchable = useMemo(() => !readonly, [readonly]);

  return (
    <Presentation.Row>
      <Presentation.Label>Record</Presentation.Label>
      <Presentation.Value>
        <Select<RecordFilter.Item, true>
          styles={selectStyles}
          components={{
            DropdownIndicator: null,
          }}
          menuIsOpen={!isSearchable ? false : undefined}
          isMulti={true}
          inputValue={input}
          onInputChange={handleInputChange}
          onChange={handleChange}
          theme={selectTheme}
          value={normalizedValue}
          isDisabled={readonly}
          closeMenuOnSelect={false}
          isSearchable={isSearchable}
          isClearable={!readonly}
          options={query.data?.results ?? []}
          isLoading={query.isLoading || query.isFetching}
          noOptionsMessage={noOptionsMessage}
          placeholder={`Search records`}
          loadingMessage={loadingMessage}
          getOptionLabel={o => o.name}
          getOptionValue={o => o.id}
          menuPlacement="auto" />
      </Presentation.Value>
    </Presentation.Row>
  );
};
